import { useCallback } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { useIntl } from 'react-intl'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import CircularProgress from '@mui/material/CircularProgress'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import StopIcon from '@mui/icons-material/Stop'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import EditIcon from '@mui/icons-material/Edit'
import { useTeam } from '../../contexts/TeamContext'
import useServicesResources from '../resources/api/v1/admin/docker/useServicesResources'
import { hasKnownStatus } from './ServiceStatusIndicator'

function ServiceActionButtons({ service, project, pendingTask, onUpdate }) {
  const intl = useIntl()
  const { team } = useTeam()
  const { start, stop, restart } = useServicesResources(team?.slug, project?.id)

  const hasPendingTask = Boolean(pendingTask)
  const statusUnknown = !hasKnownStatus(service)
  const isStarting = pendingTask?.taskType === 'TYPE::START_SERVICE'
  const isStopping = pendingTask?.taskType === 'TYPE::STOP_SERVICE'
  const isRestarting = pendingTask?.taskType === 'TYPE::RESTART_SERVICE'

  const handleStart = useCallback(() => {
    start(service.slug)
      .then(() => {
        if (onUpdate) onUpdate()
      })
  }, [service.slug, start, onUpdate])

  const handleStop = useCallback(() => {
    stop(service.slug)
      .then(() => {
        if (onUpdate) onUpdate()
      })
  }, [service.slug, stop, onUpdate])

  const handleRestart = useCallback(() => {
    restart(service.slug)
      .then(() => {
        if (onUpdate) onUpdate()
      })
  }, [service.slug, restart, onUpdate])

  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      {isStarting ? (
        <IconButton
          size="small"
          disabled
          title={intl.formatMessage({ id: 'dockerServices.actions.start' })}
        >
          <CircularProgress size={20} />
        </IconButton>
      ) : isStopping ? (
        <IconButton
          size="small"
          disabled
          title={intl.formatMessage({ id: 'dockerServices.actions.stop' })}
        >
          <CircularProgress size={20} />
        </IconButton>
      ) : service.expectedState === 'stopped' ? (
        <IconButton
          size="small"
          onClick={handleStart}
          disabled={hasPendingTask || statusUnknown}
          title={intl.formatMessage({ id: 'dockerServices.actions.start' })}
        >
          <PlayArrowIcon />
        </IconButton>
      ) : (
        <IconButton
          size="small"
          onClick={handleStop}
          disabled={hasPendingTask || statusUnknown}
          title={intl.formatMessage({ id: 'dockerServices.actions.stop' })}
        >
          <StopIcon />
        </IconButton>
      )}
      <IconButton
        size="small"
        onClick={handleRestart}
        disabled={hasPendingTask || statusUnknown || service.expectedState === 'stopped'}
        title={intl.formatMessage({ id: 'dockerServices.actions.restart' })}
      >
        {isRestarting ? <CircularProgress size={20} /> : <RestartAltIcon />}
      </IconButton>
      <IconButton
        component={RouterLink}
        to={`/teams/${team.slug}/docker/projects/${project?.slug}/services/${service.slug}/settings`}
        size="small"
        title={intl.formatMessage({ id: 'dockerServices.actions.edit' })}
      >
        <EditIcon />
      </IconButton>
    </Box>
  )
}

export default ServiceActionButtons
